

import React from 'react';
import { Box, Button, Grid, IconButton, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { trans } from '../../../../_providers/Translation';
import Api from '../../../../_helpers/Api';

const DEFAULT_PAGE_SIZE = 10;

function formatExportDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${day} ${month} ${year} / ${time}`;
}

const DataExportsReady = () => {
    const [state, setState] = React.useState({
        loading: true,
        items: [],
        total: 0,
        pages: 1,
        page: 1,
        perPage: DEFAULT_PAGE_SIZE,
    });

    const fetchAllExports = (page = 1, perPage = DEFAULT_PAGE_SIZE) => {
        setState(prev => ({ ...prev, loading: true }));
        Api.get('/data-exports/all', { params: { page, per_page: perPage } })
            .then(res => {
                const data = res.data || {};
                setState(prev => ({
                    ...prev,
                    loading: false,
                    items: Array.isArray(data.items) ? data.items : [],
                    total: data.total || 0,
                    pages: data.pages || 1,
                    page: page,
                    perPage: perPage,
                }));
            })
            .catch(err => {
                setState(prev => ({ ...prev, loading: false, items: [] }));
                console.error('Failed to fetch ready exports:', err);
            });
    };

    React.useEffect(() => {
        fetchAllExports(1, state.perPage);
        // eslint-disable-next-line
    }, []);

    const handlePrev = () => {
        if (state.page > 1) fetchAllExports(state.page - 1, state.perPage);
    };
    const handleNext = () => {
        if (state.page < state.pages) fetchAllExports(state.page + 1, state.perPage);
    };

    const handleRefresh = () => {
        fetchAllExports(state.page, state.perPage);
    };

    return (
        <Paper elevation={1} style={{ marginTop: 32, padding: 16 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {trans('headings.myExports') || 'My Exports'}
                </Typography>
                <Button size="small" variant="outlined" onClick={handleRefresh}>
                    {trans('buttons.refresh') || 'Refresh'}
                </Button>
            </Box>
            {state.loading ? (
                <Typography variant="body2">{trans('labels.loading') || 'Loading...'}</Typography>
            ) : state.items.length === 0 ? (
                <Typography variant="body2" color="textSecondary">{trans('messages.noDataFound') || 'No exports found.'}</Typography>
            ) : (
                <>
                    <Grid container spacing={1}>
                        {state.items.map((exp, idx) => (
                            <Grid item xs={12} key={exp.id || idx}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ borderBottom: '1px solid #eee', padding: 8 }}>
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="body2">
                                            <b>{trans('labels.exportId') || 'ID'}:</b> {exp.id || '-'}
                                            {exp.user_id && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.user') || 'User ID'}:</b> {exp.user_id}</span>
                                            )}
                                            {exp.user_name && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.user') || 'User'}:</b> {exp.user_name}</span>
                                            )}
                                            {exp.status && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.status') || 'Status'}:</b> {trans('exportStatus.' + exp.status) || exp.status}</span>
                                            )}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#888', fontSize: 12 }}>
                                            <b>{trans('labels.date') || 'Date'}:</b> {formatExportDate(exp.created_at)}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={async () => {
                                                try {
                                                    const response = await Api.get(`/data-exports/download/${exp.id}`, { responseType: 'blob' });
                                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.setAttribute('download', exp.filename || `export-${exp.id}.zip`);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();
                                                } catch (err) {
                                                    alert('Download failed.');
                                                    console.error('Download error:', err);
                                                }
                                            }}
                                        >
                                            {trans('buttons.download') || 'Download'}
                                        </Button>
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={async () => {
                                                if (!window.confirm(trans('messages.confirmDelete') || 'Delete this export?')) return;
                                                try {
                                                    await Api.delete(`/data-exports/${exp.id}`);
                                                    handleRefresh();
                                                } catch (err) {
                                                    alert('Delete failed.');
                                                    console.error('Delete error:', err);
                                                }
                                            }}
                                            title={trans('buttons.delete') || 'Delete'}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                    <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={2}>
                        <Button size="small" onClick={handlePrev} disabled={state.page === 1} style={{ minWidth: 36 }}>&lt;</Button>
                        <Typography variant="body2" style={{ margin: '0 8px' }}>{trans('labels.page') || 'Page'} {state.page} / {state.pages}</Typography>
                        <Button size="small" onClick={handleNext} disabled={state.page === state.pages} style={{ minWidth: 36 }}>&gt;</Button>
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default DataExportsReady;